import { useState, useCallback } from 'react';
import { GroupedProduct } from '../types';
import { productService, handleApiError } from '../services';
import { API_BASE_URL } from '../config/constants';

/** Resolve image_url: convert relative /uploads/... paths to absolute URLs */
const resolveImageUrl = (url: string | null | undefined): string | undefined => {
  if (!url) return undefined;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/uploads')) {
    const baseUrl = API_BASE_URL.replace('/api/v1', '');
    return `${baseUrl}${url}`;
  }
  return url;
};

export interface GroupedProductFilters {
  search?: string;
  limit?: number;
  offset?: number;
  family_code?: string;
  subfamily_code?: string;
  brand_code?: string;
  category_item_code?: string;
}

export interface UseGroupedProductsReturn {
  groups: GroupedProduct[];
  isLoading: boolean;
  error: string | null;
  total: number;
  hasMore: boolean;
  loadGroups: (filters?: GroupedProductFilters) => Promise<void>;
  clearError: () => void;
}

export const useGroupedProducts = (): UseGroupedProductsReturn => {
  const [groups, setGroups] = useState<GroupedProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  const loadGroups = useCallback(async (filters: GroupedProductFilters = {}) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await productService.getGroupedCatalog({
        limit: filters.limit || 20,
        offset: filters.offset || 0,
        search: filters.search,
        family_code: filters.family_code,
        subfamily_code: filters.subfamily_code,
        brand_code: filters.brand_code,
        category_item_code: filters.category_item_code,
      });

      // Resolve image URLs
      const resolved: GroupedProduct[] = response.groups.map((g) => ({
        ...g,
        representative_image_url: resolveImageUrl(g.representative_image_url) || null,
        color_swatches: g.color_swatches.map((s) => ({
          ...s,
          color_image_url: resolveImageUrl(s.color_image_url),
        })),
      }));

      if ((filters.offset || 0) === 0) {
        setGroups(resolved);
      } else {
        setGroups((prev) => [...prev, ...resolved]);
      }
      setTotal(response.total_count);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  const hasMore = groups.length < total;

  return {
    groups,
    isLoading,
    error,
    total,
    hasMore,
    loadGroups,
    clearError: useCallback(() => setError(null), []),
  };
};
